import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";
import { useAnalyticsData } from "../../analytics/hooks/use-analytics-data";
import { usePendo } from "../../analytics/hooks/use-pendo-watch";
import { useClaims } from "../../generic/hooks/use-claims";
import type { Columns } from "../types";

export const useTriggerPendoUpdate = (): {
  accountUpdated: (row: Columns) => Promise<void>;
} => {
  const pendo = usePendo();
  const { user } = useAuth0();
  const claims = useClaims();

  const { data: currentSessionData } = useAnalyticsData(claims?.sub, claims?.role, user?.email);

  const accountUpdated = useCallback(
    async ({ id, name, business_name, usage, churned, ...limits }: Columns) => {
      if (pendo && claims && currentSessionData) {
        await pendo.flushNow();
        pendo.teardown();

        const { contributors = 0, super_admins = 0 } = limits;

        pendo.initialize({
          account: {
            id: business_name,
            accountName: name,
            uniqueAccountId: id,
            usage,
            churned,
            limit_contributors: contributors,
            limit_super_admins: super_admins,
          },
          visitor: {
            id: claims.sub,
          },
        });
        pendo.track("ce_admin_account_updated", {
          account_id: id,
          account_name: name,
          business_name: business_name,
          usage: usage,
          churned: churned,
          limit_contributors: contributors,
          limit_super_admins: super_admins,
        });
        // Now close and reinitiliase pendo with the logged in user data
        await pendo.flushNow();
        pendo.teardown();
        pendo.initialize(currentSessionData);
      }
    },
    [currentSessionData, pendo, claims],
  );

  return {
    accountUpdated,
  };
};
